import React, { useState } from "react";
import Modal from "react-modal";

import { Box } from "styles/globalComponents";

import { LoadingText } from "Components/LoadingText";
import PageHeader from "Components/Pages/Header";
import { Pagination } from "Components/Pagination";
import Breadcrumb from "Components/Shared/Breadcrumb";

import { Button } from "Components/Button";
import { SelectAppointment } from "Components/SelectSchedule";
import { createToast } from "Utils/toastFunc";
import { rescheduleV2 } from "Requests/SchedulePartners";
import { useMutation, useQueryClient } from "react-query";
import { GetClientBudgets } from "Requests/Partners/Budgets";
import { Filter } from "./components/Filters";
import { ListBudgets } from "./components/ListBudgets";
import { ButtonsHolder, PageContainer, PageContent } from "./styles";
import { useGetBudgetsClients } from "./hooks/useGetBudgetsClient";
import { ScheduleInfo } from "./components/TableItem";

export const ClientsBudgetsUnit = () => {
  const {
    data,
    setBudgetsFilters,
    isLoading: loading,
    isError,
    pagination,
    handlePagination,
  } = useGetBudgetsClients();

  const [appointment, setAppointment] = useState("");
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);

  const [scheduleInfo, setScheduleInfo] = useState<null | ScheduleInfo>(null);

  const rescheduleAppointmentMutation = useMutation({
    mutationFn: rescheduleV2,
  });

  const client = useQueryClient();

  const queries = client.getQueriesData<GetClientBudgets>({
    queryKey: "clients-budgets-units",
  });

  const updateCacheData = (oldData: GetClientBudgets | undefined) => {
    if (!oldData) return {} as GetClientBudgets;

    const changedData = oldData?.data.map((item) => {
      if (item.cli_pre_budget.acuity_id === scheduleInfo?.id) {
        return {
          ...item,
          cli_pre_budget: {
            ...item.cli_pre_budget,
            appointment_at: appointment.replace("T", " ").replace("-0300", ""),
          },
        };
      }

      return item;
    });

    return {
      data: changedData,
      meta: oldData?.meta,
    };
  };

  const generateNewSchedule = async () => {
    try {
      if (!appointment) {
        throw new Error("Horário não selecionado");
      }

      if (!scheduleInfo?.clientId) throw new Error("");

      await rescheduleAppointmentMutation.mutateAsync({
        appointment,
        id: scheduleInfo.id, // acuity_id,
        client_id: scheduleInfo.clientId,
      });

      queries.forEach(([key]) => {
        client.setQueryData(key, updateCacheData);
      });

      createToast("success", "Reagendamento realizado com sucesso");

      setIsScheduleModalOpen(false);
    } catch (errors: any) {
      const error = errors?.response?.data?.errors?.[0]?.message;

      if (error) {
        createToast("error", error);
        return;
      }

      if (!error) {
        createToast("error", "Não foi possível realizar o reagendamento");
        return;
      }

      if (errors?.message) {
        createToast("error", errors.message);
      }
    }
  };

  const onSetScheduleInfo = (schedule: ScheduleInfo) => {
    setIsScheduleModalOpen(true);
    setScheduleInfo(schedule);
  };

  const onBackToDetails = () => {
    setIsScheduleModalOpen(false);
    setScheduleInfo(null);
  };

  if (isError) {
    return (
      <PageContainer className="entry-app-content ">
        <PageContent className="content-holder ">
          <Box mt="100">
            <h5 className="text-center text-purple">
              Não foi possível obter a listagem de orçamentos de parceiros !
              Atualize a página.
            </h5>
          </Box>
        </PageContent>
      </PageContainer>
    );
  }

  return (
    <PageContainer className="entry-app-content ">
      <PageContent className="content-holder">
        <div className="page-heading">
          <div className="wrapper-header-content">
            <PageHeader title="Orçamentos Parceiros">
              <Breadcrumb
                list={[
                  {
                    path: "#",
                    label: "Orçamentos",
                    current: false,
                  },
                  {
                    path: "#",
                    label: "Parceiros",
                    current: true,
                  },
                ]}
              />
            </PageHeader>
          </div>
        </div>

        <div className="page-content">
          <Filter
            searchBudgets={setBudgetsFilters}
            productsQuantity={data?.meta?.pagination.total || 0}
          />

          {loading ? (
            <Box mt="100">
              <LoadingText text="Carregando lista de orçamentos de parceiros. Aguarde!" />
            </Box>
          ) : null}

          {data?.data.length && !loading ? (
            <>
              <ListBudgets
                data={data.data}
                setScheduleInfo={onSetScheduleInfo}
              />

              <Pagination
                key={pagination?.current_page}
                totalRecords={pagination?.total_pages}
                currentPageSelected={pagination?.current_page}
                pageLimit={1}
                pageNeighbours={1}
                onPageChanged={(e) => handlePagination(e)}
              />
            </>
          ) : null}

          {!data?.data.length && !loading ? (
            <Box mt="60">
              <h3 className="text-center text-purple">
                Nenhum orçamento encontrado
              </h3>
            </Box>
          ) : null}
        </div>
      </PageContent>

      {scheduleInfo && (
        <Modal
          shouldCloseOnOverlayClick={false}
          isOpen={isScheduleModalOpen}
          onRequestClose={() => setIsScheduleModalOpen(false)}
        >
          <SelectAppointment
            clientId={scheduleInfo.clientId}
            onSelectAppointment={setAppointment}
            preOrderId={scheduleInfo.preOrderId}
            zipcode={scheduleInfo.zipcode}
          >
            <ButtonsHolder>
              {appointment && (
                <Button
                  onClick={generateNewSchedule}
                  loading={rescheduleAppointmentMutation.isLoading}
                  disabled={rescheduleAppointmentMutation.isLoading}
                >
                  Prosseguir com o reagendamento
                </Button>
              )}
              <Button
                outlined
                disabled={rescheduleAppointmentMutation.isLoading}
                onClick={onBackToDetails}
              >
                Voltar para detalhes
              </Button>
            </ButtonsHolder>
          </SelectAppointment>
        </Modal>
      )}
    </PageContainer>
  );
};
